import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AimdocLogo } from '../brand/AimdocLogo';
import { toast } from 'react-toastify';
import useApi from '../api';

const SCOPE_DESCRIPTIONS: Record<string, string> = {
    api: 'Read conversations, contacts, and visitor data, and manage webhooks for your organization',
};

const OAuthConsentScreen: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loadingAllow, setLoadingAllow] = useState(false);
    const [loadingDeny, setLoadingDeny] = useState(false);
    const { post } = useApi();

    const requestId = searchParams.get('request_id');
    const scope = searchParams.get('scope');
    const clientName = searchParams.get('client_name') || 'the application';

    useEffect(() => {
        if (!requestId) {
            toast.error('Invalid authorization request parameters.');
            navigate('/');
        }
    }, [requestId, navigate]);

    const submitDecision = async (decision: 'allow' | 'deny') => {
        const setLoading = decision === 'allow' ? setLoadingAllow : setLoadingDeny;
        setLoading(true);
        try {
            const response = await post('/oauth/consent/submit', {
                request_id: requestId,
                decision,
            });

            if (response && response.data && typeof response.data.redirectTo === 'string') {
                window.location.href = response.data.redirectTo;
            } else {
                console.error(`Backend did not provide a valid redirectTo URL for '${decision}'. Response:`, response);
                toast.error('Failed to process consent. No redirect information received.');
                setLoading(false);
            }
        } catch (error: any) {
            console.error(`Error submitting '${decision}' consent:`, error);
            const detail = error.response?.data?.detail;
            toast.error(detail || 'Failed to process consent. Please try again.');
            setLoading(false);
        }
    };

    if (!requestId) {
        return null;
    }

    const scopeDescriptions = (scope || 'api')
        .split(' ')
        .filter(Boolean)
        .map((s) => SCOPE_DESCRIPTIONS[s] || s);

    return (
        <div className="flex justify-center items-center min-h-screen w-full bg-muted/40">
            <Card className="w-full max-w-md shadow-lg">
                <CardHeader className="items-center text-center">
                    <AimdocLogo className="mb-4 h-10 w-auto text-foreground" />
                    <CardTitle>Authorize {clientName}</CardTitle>
                    <CardDescription>
                        Allow <strong>{clientName}</strong> to access your Aimdoc account?
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="text-sm text-muted-foreground p-3 border rounded-md">
                        <p className="font-medium mb-1">This will allow {clientName} to:</p>
                        <ul className="list-disc list-inside pl-2">
                            {scopeDescriptions.map((description) => (
                                <li key={description}>{description}</li>
                            ))}
                        </ul>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        You will be redirected back to {clientName} after granting access.
                        By authorizing, you agree to Aimdoc's Terms and Privacy Policy.
                    </p>
                </CardContent>
                <CardFooter className="flex justify-end space-x-2">
                    <Button
                        variant="outline"
                        onClick={() => submitDecision('deny')}
                        disabled={loadingDeny || loadingAllow}
                    >
                        {loadingDeny ? 'Denying...' : 'Deny'}
                    </Button>
                    <Button
                        onClick={() => submitDecision('allow')}
                        disabled={loadingAllow || loadingDeny}
                    >
                        {loadingAllow ? 'Allowing...' : 'Allow'}
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
};

export default OAuthConsentScreen;
